import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MdClose, MdExpandMore, MdExpandLess } from "react-icons/md";
import { useMediaQuery, useTheme, Snackbar } from "@mui/material";

const StatusUpdatePanel = ({ updates, onDismiss, visible = true }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [isExpanded, setIsExpanded] = useState(false);
  const [displayUpdates, setDisplayUpdates] = useState([]);
  const [tooltipName, setTooltipName] = useState(null);

  // Sort and deduplicate updates (keep latest status for each user)
  useEffect(() => {
    const uniqueUpdates = {};
    updates.forEach((update) => {
      uniqueUpdates[update.friendId] = update;
    });
    setDisplayUpdates(Object.values(uniqueUpdates));
  }, [updates]);

  // We'll let the parent control visibility so exit animations can run

  const statusEmojis = {
    Studying: "📚",
    Free: "✨",
    Collaboration: "🤝",
    Busy: "⏱️",
    Offline: "🚪",
  };

  const statusColors = {
    Studying: "#3b82f6",
    Free: "#10b981",
    Collaboration: "#f59e0b",
    Busy: "#ef4444",
    Offline: "#6b7280",
  };

  const collapsedView = (
    <div className="flex items-center justify-between px-4 py-3 pr-12">
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <div className="flex -space-x-2 flex-shrink-0">
          {displayUpdates.slice(0, 3).map((update) => (
            <div
              key={update.friendId}
              className="w-8 h-8 rounded-full bg-gray-200 border-2 border-white flex items-center justify-center text-xs font-bold text-gray-700 flex-shrink-0 cursor-pointer"
              title={!isMobile ? update.friendName : undefined}
              onClick={() => isMobile && setTooltipName(update.friendName)}
            >
              {update.friendName.charAt(0).toUpperCase()}
            </div>
          ))}
          {displayUpdates.length > 3 && (
            <div className="w-8 h-8 rounded-full bg-gray-300 border-2 border-white flex items-center justify-center text-xs font-bold text-gray-700 flex-shrink-0">
              +{displayUpdates.length - 3}
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-900 truncate">
            {displayUpdates.length}{" "}
            {displayUpdates.length === 1 ? "friend" : "friends"} updated
          </p>
          <p className="text-xs text-gray-500 truncate">
            {displayUpdates.length === 1
              ? `${displayUpdates[0].friendName} is ${
                  displayUpdates[0].status === "Offline"
                    ? "off campus"
                    : displayUpdates[0].status === "Collaboration"
                      ? "seeking a study group"
                      : `now ${displayUpdates[0].status}`
                }`
              : "Tap to see who"}
          </p>
        </div>
      </div>
    </div>
  );

  const expandedView = (
    <div>
      <div className="flex items-center justify-between px-4 py-3 border-b border-black/10">
        <div>
          <p className="text-sm font-semibold text-gray-900">Status Updates</p>
          <p className="text-xs text-gray-500">
            {displayUpdates.length} friends
          </p>
        </div>
        <button
          onClick={() => setIsExpanded(false)}
          className="p-1 text-gray-500 hover:text-gray-900 transition-colors flex-shrink-0"
        >
          <MdExpandLess size={20} />
        </button>
      </div>
      <div
        className={
          isMobile ? "max-h-96 overflow-y-auto" : "max-h-72 overflow-y-auto"
        }
      >
        {displayUpdates.map((update) => (
          <div
            key={update.friendId}
            className="px-4 py-3 border-b border-black/5 flex items-center gap-3 hover:bg-black/5 transition-colors"
          >
            <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center text-sm font-bold text-gray-700 flex-shrink-0">
              {update.friendName.charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">
                {update.friendName}
              </p>
              <div
                className="text-xs font-semibold mt-0.5 flex items-center gap-1.5"
                style={{ color: statusColors[update.status] || "#9ca3af" }}
              >
                <span>{statusEmojis[update.status] || "•"}</span>
                <span>
                  {update.status === "Offline"
                    ? "Off Campus"
                    : update.status === "Collaboration"
                      ? "Seeking a study group"
                      : update.status}
                </span>
              </div>
            </div>
            <div
              className="w-2 h-2 rounded-full flex-shrink-0"
              style={{
                backgroundColor: statusColors[update.status] || "#9ca3af",
              }}
            />
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <AnimatePresence>
      {visible && displayUpdates.length > 0 && (
        <motion.div
          initial={{ y: -100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -100, opacity: 0 }}
          transition={{ type: "spring", stiffness: 400, damping: 35 }}
          className={`fixed z-[1200] left-0 right-0 pointer-events-auto ${
            isMobile ? "top-14 px-3 pt-3" : "top-16 px-4 pt-4"
          }`}
        >
          <div
            className={`mx-auto ${
              isMobile ? "w-full" : "max-w-md"
            } bg-white border border-black/10 rounded-2xl shadow-2xl overflow-hidden backdrop-blur-xl bg-white/80`}
          >
            {isExpanded ? expandedView : collapsedView}

            {!isExpanded && (
              <button
                onClick={onDismiss}
                className="absolute top-3 right-3 p-1.5 rounded-full bg-black/10 hover:bg-black/20 text-gray-500 hover:text-gray-900 transition-all z-10 flex-shrink-0"
                title={!isMobile ? "Close" : undefined}
              >
                <MdClose size={16} />
              </button>
            )}

            {!isExpanded && (
              <button
                onClick={() => setIsExpanded(true)}
                className="absolute top-3 right-12 p-1.5 rounded-full bg-black/10 hover:bg-black/20 text-gray-500 hover:text-gray-900 transition-all z-10 flex-shrink-0"
                title={!isMobile ? "Expand" : undefined}
              >
                <MdExpandMore size={16} />
              </button>
            )}
          </div>
        </motion.div>
      )}
      {/* Mobile tooltip snackbar for showing name on tap */}
      <Snackbar
        open={!!tooltipName}
        autoHideDuration={1500}
        onClose={() => setTooltipName(null)}
        message={tooltipName}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        ContentProps={{
          sx: {
            backgroundColor: "#1d1d1f",
            color: "#ffffff",
            fontSize: "14px",
            fontWeight: 500,
            borderRadius: "12px",
            minWidth: "auto",
          },
        }}
      />
    </AnimatePresence>
  );
};

export default StatusUpdatePanel;
